import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Grid, Stack, Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, styled } from '@mui/material/styles';
import { Loader } from '@alumni-ventures-group/react-shared/lib/components';
import useStyles from '../styles';
import isTransactionComplete from '../../../../../shared/helpers/isTransactionComplete';
import InvestmentStats from '../common/InvestorStats';
import { PlanContext } from '../../context/PlanContext';
import { downloadFileById } from '../../../../../shared/services/apiGateway';
import PlanningButton from '../common/PlanningButton';

export const DownloadReportSubtitleTypography = styled(Typography)(
  ({ theme }) => ({
    fontSize: 18,
    fontWeight: 400,
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
      fontWeight: 400,
    },
  }),
);

export const DownloadReportButtonTypography = styled(Typography)(
  ({ theme }) => ({
    fontSize: 28,
    fontWeight: 600,
    lineHeight: '30.46px',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
    },
  }),
);

const StyledButton = styled(LoadingButton)(({ theme }) => ({
  width: '100%',
  minHeight: '95px',
  borderRadius: '10px',
  border: '2px solid #7B5599',
  color: '#000',
  backgroundColor: '#fff',
  boxShadow: '0px 4px 8px 0px #00000040', // Added drop shadow
  padding: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&:disabled': {
    boxShadow: 'none',
    '.MuiTypography-root': {
      color: '#646777',
    },
  },
  '&.MuiLoadingButton-loading': {
    'padding-right': '10%',
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '60px',
  },
  '.MuiLoadingButton-loadingIndicator': {
    left: 'unset',
    right: '10px',
    '.MuiCircularProgress-root': {
      [theme.breakpoints.up('sm')]: {
        height: '24px !important',
        width: '24px !important',
      },
    },
  },
}));

const InvestmentSnapshot = ({ onContinue }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { userPortfolio, planningConfig, userPPR } = useContext(PlanContext);

  const [downloadLoading, setDownloadLoading] = useState(false);

  const filteredTransactions = userPortfolio?.transactions?.length > 0
    ? userPortfolio.transactions.filter(
      (transaction) => isTransactionComplete(transaction)
            && transaction.vintageFundType !== 'FUNDRAISE',
    )
    : [];

  const firstDateOfInvestment = filteredTransactions.length > 0
    ? filteredTransactions.reduce((earliest, transaction) => {
      const dates = [
        transaction.fundsTransferDate,
        transaction.proofOfAccreditationDate,
        transaction.subscriptionAgreementDate,
      ];

      const latestDate = Math.max(
        ...dates.map((date) => new Date(date).getTime()),
      );

      const transactionDate = transaction.completionDate
        ? new Date(transaction.completionDate).getTime()
        : latestDate;

      return transactionDate < earliest ? transactionDate : earliest;
    }, Infinity)
    : null;

  const firstDateOfInvestmentFormatted = firstDateOfInvestment
    ? new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(firstDateOfInvestment))
    : null;

  let totalInvestments = 0;
  let totalNumberOfInvestments = 0;

  filteredTransactions.forEach((transaction) => {
    totalNumberOfInvestments++;
    totalInvestments += parseFloat(transaction.amount);
  });

  const yearDiff = (dateFrom, dateTo) => (
    (dateTo.getMonth()
        - dateFrom.getMonth()
        + 12 * (dateTo.getFullYear() - dateFrom.getFullYear()))
      / 12
  );

  const numberOfYears = yearDiff(new Date(firstDateOfInvestment), new Date());

  const averageAnnualInvestment = numberOfYears < 1
    ? totalInvestments
    : Math.round(totalInvestments / numberOfYears);

  const isLoading = !userPortfolio || !firstDateOfInvestmentFormatted;

  if (!planningConfig?.investmentSnapshot) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Loader />
      </Box>
    );
  }

  const downloadPPR = async (event) => {
    event.preventDefault();
    if (!userPPR) {
      return;
    }
    setDownloadLoading(true);
    const response = await downloadFileById(userPPR.id);

    window.open(response.fileUrl, '_blank');
    setDownloadLoading(false);
  };

  return (
    <Box className={`${classes.content} 'margin'`}>
      {isMobile ? (
        <Stack spacing={4} alignItems="center">
          <Typography
            variant="h1ExtraLarge"
            component="h1"
            style={{ color: '#191919', textAlign: 'center', padding: '0 20px' }}
          >
            {planningConfig?.investmentSnapshot?.title}
          </Typography>
          <Typography
            variant="body2Inter"
            style={{ color: '#797979', padding: '0 20px' }}
            dangerouslySetInnerHTML={{
              __html:
                planningConfig?.investmentSnapshot?.subtitle?.richtext || '',
            }}
          />
          <InvestmentStats
            firstDateOfInvestment={firstDateOfInvestmentFormatted}
            totalNumberOfInvestments={totalNumberOfInvestments}
            averageAnnualInvestment={averageAnnualInvestment}
            isMobile={isMobile}
            isLoading={isLoading}
          />
          {userPPR && (
            <Stack spacing={4} className={classes.pdfDownloader}>
              <Typography
                variant="h1ExtraLarge"
                component="h2"
                style={{ color: '#1B9390' }}
              >
                {planningConfig?.investmentSnapshot?.portfolioReportDownloadTitle}
              </Typography>
              <DownloadReportSubtitleTypography
                component="div"
                dangerouslySetInnerHTML={{
                  __html:
                    planningConfig?.investmentSnapshot
                      ?.portfolioReportDownloadBodyCopy?.richtext || '',
                }}
              />
              <StyledButton
                variant="outlined"
                onClick={downloadPPR}
                disabled={!userPPR}
                loading={downloadLoading}
              >
                <DownloadReportButtonTypography>
                  {
                    !downloadLoading ? planningConfig?.investmentSnapshot
                      ?.downloadReportButtonCopy : 'Downloading...'
                  }
                </DownloadReportButtonTypography>
              </StyledButton>
            </Stack>
          )}
          <PlanningButton text="Start Planning" onClick={onContinue} />
        </Stack>
      ) : (
        <>
          <Typography
            variant="h1ExtraLarge"
            component="h1"
            style={{ color: '#191919' }}
          >
            {planningConfig?.investmentSnapshot?.title}
          </Typography>
          <Typography
            variant="body2Inter"
            style={{ color: '#797979', padding: 15 }}
            dangerouslySetInnerHTML={{
              __html:
                planningConfig?.investmentSnapshot?.subtitle?.richtext || '',
            }}
          />
          <Grid
            container
            sx={{
              marginTop: { md: '60px', lg: '100px' },
              justifyContent: 'space-around',
            }}
          >
            <Grid item md={12} lg={6}>
              <InvestmentStats
                firstDateOfInvestment={firstDateOfInvestmentFormatted}
                totalNumberOfInvestments={totalNumberOfInvestments}
                averageAnnualInvestment={averageAnnualInvestment}
                isMobile={isMobile}
                isLoading={isLoading}
              />
            </Grid>
            {userPPR && (
              <Grid item md={12} lg={6} className={classes.pdfDownloader}>
                <Stack spacing={4}>
                  <Typography
                    variant="h1ExtraLarge"
                    component="h2"
                    style={{ color: '#1B9390' }}
                  >
                    {
                      planningConfig?.investmentSnapshot
                        ?.portfolioReportDownloadTitle
                    }
                  </Typography>
                  <DownloadReportSubtitleTypography
                    component="div"
                    dangerouslySetInnerHTML={{
                      __html:
                        planningConfig?.investmentSnapshot
                          ?.portfolioReportDownloadBodyCopy?.richtext || '',
                    }}
                  />
                  <StyledButton
                    variant="outlined"
                    onClick={downloadPPR}
                    disabled={!userPPR}
                    loading={downloadLoading}
                  >
                    <DownloadReportButtonTypography>
                      {
                        !downloadLoading ? planningConfig?.investmentSnapshot
                          ?.downloadReportButtonCopy : 'Downloading...'
                      }
                    </DownloadReportButtonTypography>
                  </StyledButton>
                </Stack>
              </Grid>
            )}
          </Grid>
          <Box mt={6}>
            <PlanningButton text="Start Planning" onClick={onContinue} />
          </Box>
        </>
      )}
    </Box>
  );
};

InvestmentSnapshot.propTypes = {
  onContinue: PropTypes.func.isRequired,
};

export default InvestmentSnapshot;
